import React, { useMemo } from 'react';
import { Box, Container, Typography, ButtonBase } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../reduxStore/Store';
import { isMultiDeptRoute } from '../constants/multiDeptRoutes';
import { NAV_GROUP_ORDER, TOOLS, Tool } from '../config/navigation';

const C = {
  page: '#f4f5f7',
  surface: '#ffffff',
  border: '#e4e7ec',
  borderHover: '#c7d2fe',
  text: '#101828',
  textSoft: '#667085',
  accent: '#2563eb',
  accentSoft: '#eef4ff',
};

const greeting = (): string => {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const CategoryLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.09em',
      textTransform: 'uppercase', color: C.textSoft, mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const ToolCard: React.FC<{ tool: Tool; big?: boolean }> = ({ tool, big }) => {
  const navigate = useNavigate();
  return (
    <ButtonBase
      focusRipple
      onClick={() => navigate(tool.to)}
      sx={{
        display: 'block',
        textAlign: 'left',
        width: '100%',
        height: '100%',
        minWidth: 0,
        borderRadius: '16px',
      }}
    >
      <Box
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          bgcolor: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: '16px',
          p: big ? 2.5 : 2,
          transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
          '&:hover': {
            borderColor: C.borderHover,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(16,24,40,0.08)',
          },
        }}
      >
        <Box
          sx={{
            width: big ? 46 : 40,
            height: big ? 46 : 40,
            borderRadius: '12px',
            bgcolor: C.accentSoft,
            color: C.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: big ? 1.75 : 1.25,
            '& .MuiSvgIcon-root': { fontSize: big ? 24 : 21 },
          }}
        >
          {tool.icon}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: big ? '1rem' : '0.92rem', color: C.text }}>
            {tool.label}
          </Typography>
          {big && <ArrowForwardIcon sx={{ fontSize: 16, color: C.textSoft }} />}
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: '0.8rem', color: C.textSoft, lineHeight: 1.45 }}>
          {tool.description}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

const CardGrid: React.FC<{ children: React.ReactNode; big?: boolean; sx?: object }> = ({ children, big, sx }) => (
  <Box
    sx={{
      display: 'grid',
      alignItems: 'stretch',
      gap: big ? 2.5 : 2,
      gridAutoRows: '1fr',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(4, 1fr)',
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Home: React.FC = () => {
  const { currentUser, permissions } = useSelector((s: RootState) => s.AuthSlice);
  const { isAllMode } = useSelector((s: RootState) => s.AppSlice);
  const allowedDepartements = useSelector((s: RootState) => s.ScopeSlice.allowedDepartements);
  const isMonoDepartement = allowedDepartements.length === 1;

  // Même règle de visibilité que DrawerMenu : en mono-département (un seul
  // dept autorisé), tout est accessible — la distinction dept/ALL est
  // artificielle avec un seul département (cf. AllModeRequiredRoute). En mode
  // "Tous départements", on ne garde que les routes compatibles ALL
  // (MULTI_DEPT_ROUTES) + celles marquées multiDeptOnly ; en mode département,
  // tout sauf les multiDeptOnly. Puis masque les outils dont la permission manque.
  const visibleTools = useMemo(() => TOOLS.filter((t) => (
    isMonoDepartement ? true
      : isAllMode ? (isMultiDeptRoute(t.to) || t.multiDeptOnly === true)
      : t.multiDeptOnly !== true
  ) && (!t.permission || permissions.includes(t.permission))), [isAllMode, isMonoDepartement, permissions]);

  // Une section par catégorie (ordre fixe `NAV_GROUP_ORDER`) : les outils
  // `primary` en carte large, les autres en carte standard, dans la même
  // section. Une catégorie sans outil visible ne s'affiche pas. Ajouter une
  // page = la déclarer dans `TOOLS` avec sa `group` — elle apparaît seule ici.
  const categories = useMemo(() => NAV_GROUP_ORDER
    .map((g) => ({
      name: g,
      primaryItems: visibleTools.filter((t) => t.group === g && t.primary),
      secondaryItems: visibleTools.filter((t) => t.group === g && !t.primary),
    }))
    .filter((g) => g.primaryItems.length > 0 || g.secondaryItems.length > 0),
  [visibleTools]);

  return (
    <Box sx={{ bgcolor: C.page, minHeight: 'calc(100vh - 60px)' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, pb: { xs: 8, md: 10 } }}>
        {/* En-tête */}
        <Box sx={{ mb: { xs: 5, md: 7 } }}>
          <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' }, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>
            {greeting()}{currentUser?.login ? `, ${currentUser.login}` : ''}
          </Typography>
          <Typography sx={{ mt: 0.5, color: C.textSoft, fontSize: '0.95rem' }}>
            Que souhaitez-vous faire aujourd'hui&nbsp;?
          </Typography>
        </Box>

        {/* Catégories */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 6 } }}>
          {categories.map((cat) => (
            <Box key={cat.name}>
              <CategoryLabel>{cat.name}</CategoryLabel>
              {cat.primaryItems.length > 0 && (
                <CardGrid big>
                  {cat.primaryItems.map((t) => (
                    <ToolCard key={t.key} tool={t} big />
                  ))}
                </CardGrid>
              )}
              {cat.secondaryItems.length > 0 && (
                <CardGrid sx={cat.primaryItems.length > 0 ? { mt: 1.5 } : undefined}>
                  {cat.secondaryItems.map((t) => (
                    <ToolCard key={t.key} tool={t} />
                  ))}
                </CardGrid>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Home;
